import { useState } from "react";
import { App } from "antd";
import {
  ZipPdfMerger,
  handleUploadFile,
  handleDeleteFile,
} from "../helpers/helpers";
import FinancialEval from "../assets/docs/financial_eval.xlsx";
import FinancialSum from "../assets/docs/financial_summary.xlsx";
import Ratios from "../assets/docs/ratios.xlsx";
import { useDispatch } from "react-redux";
import {
  getPendingKyc,
  verifyDoc,
  rejectDoc,
} from "../redux/slices/EnqMngtSlice";
import { getApproveEnt, reUploadEntDocs } from "../redux/slices/enterpriseSlice";

export const useDocUpload = ({
  onFileChange,
  uploadPath,
  fileType,
  fileTitle,
  entId,
  orgId,
  fileData,
  fileKey,
}) => {
  const { message } = App.useApp();
  const dispatch = useDispatch();

  const [file, setFile] = useState({ fileUrl: null, fileName: null });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sampleDownloaded, setSampleDownloaded] = useState(false);
  const [modal, setModal] = useState(false);

  const beforeUpload = (file) => {
    const isLt20MB = file.size / 1024 / 1024 < 20;
    if (!isLt20MB) {
      message.error("File must be smaller than 20MB!");
      return false;
    }

    let isValid = false;

    switch (fileType) {
      case "PDF":
        isValid = file.type === "application/pdf";
        break;
      case "JPG, PNG, JPEG, WEBP":
        isValid = ["image/jpeg", "image/png"].includes(file.type);
        break;
      case "XLSX":
        isValid =
          file.name.endsWith(".xlsx") ||
          file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        break;
      default:
        isValid = file.name.endsWith(".zip") || file.type === "application/pdf";
    }

    if (!isValid) {
      message.error(`Please upload a valid ${fileType} file.`);
      return false;
    }

    return true;
  };

  const uploadFile = async (file) => {
    try {
      const cleanName = file.name.replace(/\s+/g, "_");
      const cleanFile = new File([file], cleanName, { type: file.type });

      setUploading(true);
      setProgress(0);

      let res;
      if (
        cleanFile.name.endsWith(".zip") ||
        cleanFile.type === "application/x-zip-compressed"
      ) {
        res = await ZipPdfMerger(cleanFile, uploadPath, setProgress);
      } else {
        res = await handleUploadFile(cleanFile, uploadPath, setProgress);
      }

      setFile(res);
      setProgress(100);
      setUploading(false);
      onFileChange?.({ title: fileTitle, type: fileType, ...res });

      if (entId && fileData?.rejected) {
        const payload = {
          entId: entId,
          docs: {
            [fileKey]: res.fileUrl,
          },
        };
        const result = await dispatch(reUploadEntDocs(payload));
        if (result.payload.statusCode === 201) {
          dispatch(getApproveEnt(entId));
        }
      }
    } catch (err) {
      setUploading(false);
      setProgress(0);
      message.error(err?.message || "File upload failed");
    }
  };

  const deleteFile = async (fileUrl, onFileDeleted) => {
    try {
      await handleDeleteFile(fileUrl);
      setFile({ fileUrl: null, fileName: null });
      onFileDeleted?.();
      message.success("File deleted");
    } catch (err) {
      message.error(err?.message || "Failed to delete file");
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    maxCount: 1,
    accept:
      fileType === "PDF"
        ? ".pdf"
        : fileType === "JPG, PNG, JPEG, WEBP"
        ? ".jpg,.jpeg,.png,.webp"
        : fileType === "XLSX"
        ? ".xlsx"
        : ".zip,.pdf",
    beforeUpload: (file) => beforeUpload(file),
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        await uploadFile(file);
        onSuccess("ok");
      } catch (e) {
        onError(e);
      }
    },
  };

  const handleSampleDownload = (key) => {
    let fileHref = null;
    let fileName = "sample.xlsx";
    if (key === "financialSummary") {
      fileHref = FinancialSum;
      fileName = "financial_summary_sample.xlsx";
    } else if (key === "ratios") {
      fileHref = Ratios;
      fileName = "ratios_sample.xlsx";
    } else {
      fileHref = FinancialEval;
      fileName = "financial_eval_sample.xlsx";
    }
    const link = document.createElement("a");
    link.href = fileHref;
    link.download = fileName;
    link.click();
    setSampleDownloaded(true);
  };

  const handleVerifyDoc = async () => {
    const response = await dispatch(
      verifyDoc({
        docId: fileData?.docId,
      })
    );

    if (response.payload.statusCode === 201) {
      if (entId) {
        dispatch(getApproveEnt(entId));
      } else {
        dispatch(getPendingKyc(orgId));
      }
      setPreviewOpen(false);
    }
  };

  const handleReasonSubmit = async (values) => {
    const response = await dispatch(
      rejectDoc({
        docId: fileData?.docId,
        rejectReason: values.reason,
      })
    );

    if (response.payload.statusCode === 201) {
      if (entId) {
        dispatch(getApproveEnt(entId));
      } else {
        dispatch(getPendingKyc(orgId));
      }
      setModal(false);
      setPreviewOpen(false);
    }
  };

  return {
    file,
    setFile,
    uploading,
    progress,
    previewOpen,
    setPreviewOpen,
    deleteFile,
    uploadProps,
    sampleDownloaded,
    handleSampleDownload,
    handleVerifyDoc,
    handleReasonSubmit,
    modal,
    setModal,
  };
};
